<script setup lang="ts">
import type { UnwrapRef } from "vue";
import { computed } from "vue";

import CascadingSelectList from "@/components/ui/CascadingSelectList/CascadingSelectList.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import type { StatusClass } from "@/model/baseTypes";
import { statusClassNames } from "@/model/baseTypes";
import { statusClassColors } from "@/model/colors";
import type { StatusItem } from "@/store/search/searchMenu";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { keysIn } from "@/utils/record";

import SelectStatusList from "./SelectStatusList.vue";

const groupedStatuses = computed(() => {
  const statuses = useSearchMenuStore().allStatusItems;

  // Group by statusClass
  const map: Partial<Record<StatusClass, StatusItem[]>> = {};
  for (const status of statuses) {
    if (!map[status.statusClass]) {
      map[status.statusClass] = [];
    }
    map[status.statusClass]!.push(status);
  }

  return map;
});

const statusClasses = computed(() => keysIn(groupedStatuses.value));

// Items for CascadingSelectList with children property
const cascadingItems = computed(() => {
  return statusClasses.value.map((key) => {
    const count = getSelectionCount(key);
    const badge = count.selected > 0 ? `${count.selected}` : undefined;
    return {
      id: key,
      badge,
      children: groupedStatuses.value[key] ?? [],
    };
  });
});

type Children = UnwrapRef<typeof cascadingItems>[0]["children"];

// get selection count for a status class
function getSelectionCount(statusClass: StatusClass): { selected: number; total: number } {
  const statuses = groupedStatuses.value[statusClass] ?? [];
  const selectedIds = useSearchMenuStore().selectedStatusIds;
  const selected = statuses.filter((s) => selectedIds.has(s.id)).length;
  return { selected, total: statuses.length };
}

// check if all statuses in a class are selected
function isStatusClassFullySelected(statusClass: StatusClass, selectedIds: Set<string>): boolean {
  const statuses = groupedStatuses.value[statusClass];
  return statuses ? statuses.every((s) => selectedIds.has(s.id)) : false;
}

// get all fully selected status classes
function getFullySelectedClasses(selectedIds: Set<string>): Set<StatusClass> {
  const fullySelected = new Set<StatusClass>();
  for (const statusClass of statusClasses.value) {
    if (isStatusClassFullySelected(statusClass, selectedIds)) {
      fullySelected.add(statusClass);
    }
  }
  return fullySelected;
}

// toggle all statuses in a class (add if selected, remove if not)
function toggleStatusClass(
  statusClass: StatusClass,
  isSelected: boolean,
  selectedIds: Set<string>,
): void {
  const statuses = groupedStatuses.value[statusClass];
  if (!statuses) return;

  const statusIds = statuses.map((s) => s.id);
  if (isSelected) {
    statusIds.forEach((id) => selectedIds.add(id));
  } else {
    statusIds.forEach((id) => selectedIds.delete(id));
  }
}

// Marker ID for partial selections (makes Clear button active)
const PARTIAL_SELECTION_MARKER_ID = "__partial__" as StatusClass;

const selectedStatusClasses = computed({
  get: () => {
    const selectedIds = useSearchMenuStore().selectedStatusIds;
    const fullySelected = getFullySelectedClasses(selectedIds);
    const result = cascadingItems.value.filter((v) => fullySelected.has(v.id));

    // Count how many IDs are in fully selected classes
    let fullySelectedCount = 0;
    for (const statusClass of fullySelected) {
      const statuses = groupedStatuses.value[statusClass];
      if (statuses) {
        fullySelectedCount += statuses.length;
      }
    }

    // If there are more selected IDs than in fully selected classes,
    // we have partial selections - add marker to preserve them
    if (selectedIds.size > fullySelectedCount) {
      result.push({ id: PARTIAL_SELECTION_MARKER_ID, badge: undefined, children: [] });
    }

    return result;
  },
  set: (classes: Array<{ id: StatusClass; badge?: string; children: StatusItem[] }>) => {
    // Filter out the marker entry
    const validClasses = classes.filter((c) => c.id !== PARTIAL_SELECTION_MARKER_ID);

    // If input is truly empty (Clear button), clear everything including partial selections
    if (classes.length === 0) {
      useSearchMenuStore().selectedStatusIds = new Set();
      return;
    }

    // Otherwise, use diffing logic to handle selection changes
    const selectedIds = new Set(useSearchMenuStore().selectedStatusIds);

    // Determine previous and new state
    const previouslySelected = getFullySelectedClasses(selectedIds);
    const newlySelected = new Set(validClasses.map((c) => c.id));

    // Only modify status classes that changed state
    for (const statusClass of statusClasses.value) {
      const wasSelected = previouslySelected.has(statusClass);
      const isSelected = newlySelected.has(statusClass);

      if (wasSelected !== isSelected) {
        toggleStatusClass(statusClass, isSelected, selectedIds);
      }
    }

    useSearchMenuStore().selectedStatusIds = selectedIds;
  },
});
</script>

<template>
  <CascadingSelectList
    v-model="selectedStatusClasses"
    :items="cascadingItems"
    :header="$t('general.statusClasses')"
  >
    <template #trigger="{ item }">
      <div class="status-class-item">
        <TypeDot :color="statusClassColors[item.id]" />
        <span>
          {{ $t(statusClassNames[item.id]) }}
        </span>
      </div>
    </template>

    <template #submenu="{ children }">
      <SelectStatusList :statuses="children as Children" />
    </template>
  </CascadingSelectList>
</template>

<style scoped>
.status-class-item {
  display: flex;
  gap: 16px;
  align-items: center;
  min-width: 180px; /* this is needed to correctly set position for the status dropdown because of the checkmark on the MenuListItem */
}
</style>
